import {
  ReactNode, useCallback, useEffect, useMemo, useState,
} from 'react';
import { StylesConfig } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Trash2 } from 'lucide-react';

import { commonSelectStyles } from '../Select/ReactSelect';

import classes from './keyValueInput.module.css';

type Option = { value: string; label: string };

type KeyValueInputProps = {
  value: Record<string, string[]>;
  onChange: (value: Record<string, string[]>) => void;
  keySuggestions?: Option[];
  valueSuggestions?: Record<string, Option[]>;
  disabled?: boolean;
  disableSpaces?: boolean;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  // Plain `<input>` for the key column instead of a CreatableSelect.
  // Use when there are no key suggestions and you want a clean text-input feel.
  keyVariant?: 'select' | 'input';
  // Lower-cased keys that should surface an inline "already in use" error on
  // the key input. Display-only; values still emit. Consumer can wrap this
  // component in a Controller with rules.validate to block submit.
  reservedKeys?: string[];
  // Custom label/content for the add button (e.g. <Plus /> + "Add parameter").
  addLabel?: ReactNode;
  // When true, the component starts with zero rows and the Add button is the
  // only thing visible. Removing the last row leaves the list empty rather
  // than re-appending an empty row.
  addOnDemand?: boolean;
};

type Row = { id: string; key: string; values: string[] };

let rowIdCounter = 0;
function nextRowId(): string {
  rowIdCounter += 1;
  return `kv-row-${rowIdCounter}`;
}

function recordToRows(record: Record<string, string[]>, addOnDemand: boolean): Row[] {
  const entries = Object.entries(record);
  if (entries.length > 0) {
    return entries.map(([key, values]) => ({ id: nextRowId(), key, values }));
  }
  return addOnDemand ? [] : [{ id: nextRowId(), key: '', values: [] }];
}

function rowsToRecord(rows: Row[]): Record<string, string[]> {
  const record: Record<string, string[]> = {};
  for (const row of rows) {
    if (row.key.trim()) {
      record[row.key] = row.values;
    }
  }
  return record;
}

// Hides dropdown/clear indicators when key is selected so it reads like a plain input
const hiddenIndicator = () => ({ display: 'none' as const });

// Base key select styles (no value selected — show dropdown indicator)
const keySelectBaseStyles: StylesConfig<Option, false> = {
  ...(commonSelectStyles as StylesConfig<Option, false>),
  control: (styles, state) => ({
    ...(commonSelectStyles.control?.(styles, state as never) as object),
    minHeight: '2.75rem',
    height: '2.75rem',
  }),
};

// Key select styles when a value IS selected — hide indicators
const keySelectSelectedStyles: StylesConfig<Option, false> = {
  ...keySelectBaseStyles,
  dropdownIndicator: hiddenIndicator,
  indicatorSeparator: hiddenIndicator,
  clearIndicator: hiddenIndicator,
};

// Extends commonSelectStyles for multi value select (auto-expanding height)
const valueSelectStyles: StylesConfig<Option, true> = {
  ...(commonSelectStyles as StylesConfig<Option, true>),
  control: (styles, state) => ({
    ...(commonSelectStyles.control?.(styles, state as never) as object),
    minHeight: '2.75rem',
    height: 'auto',
  }),
  valueContainer: (styles) => ({
    ...styles,
    flexWrap: 'wrap' as const,
    padding: '0.25rem 0.5rem',
  }),
  multiValue: (styles) => ({
    ...styles,
    margin: '0.125rem',
    borderRadius: 0,
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: 'var(--color-slate-500)',
    ':hover': {
      backgroundColor: 'var(--color-slate-100)',
      color: 'var(--color-slate-700)',
    },
  }),
};

type KeyValueRowProps = {
  row: Row;
  keyVariant: 'select' | 'input';
  keySuggestions: Option[];
  valueSuggestions: Option[];
  keyError?: string;
  disabled: boolean;
  disableSpaces: boolean;
  keyPlaceholder: string;
  valuePlaceholder: string;
  onKeyChange: (newKey: string | null) => void;
  onValuesChange: (newValues: readonly Option[]) => void;
  onRemove: () => void;
};

function KeyValueRow({
  row,
  keyVariant,
  keySuggestions,
  valueSuggestions,
  keyError,
  disabled,
  disableSpaces,
  keyPlaceholder,
  valuePlaceholder,
  onKeyChange,
  onValuesChange,
  onRemove,
}: KeyValueRowProps) {
  const [keyInput, setKeyInput] = useState('');
  const [valueInput, setValueInput] = useState('');

  const sanitize = (input: string) => (
    disableSpaces ? input.replace(/\s/g, '') : input
  );

  const keyLabel = keySuggestions.find(
    (s) => s.value === row.key,
  )?.label ?? row.key;
  const selectedKeyOption = row.key
    ? { value: row.key, label: keyLabel }
    : null;

  const selectedValueOptions: Option[] = row.values.map((v) => {
    const match = valueSuggestions.find((s) => s.value === v);
    return match ?? { value: v, label: v };
  });

  const keyColumn = keyVariant === 'input' ? (
    <div className={classes.keyColumn}>
      <input
        type="text"
        className={`${classes.keyInputPlain} ${keyError ? classes.keyInputPlainError : ''}`}
        value={row.key}
        placeholder={keyPlaceholder}
        disabled={disabled}
        onChange={(e) => onKeyChange(sanitize(e.target.value))}
      />
      {keyError && <div className={classes.keyError}>{keyError}</div>}
    </div>
  ) : (
    <CreatableSelect<Option>
      isClearable
      styles={row.key ? keySelectSelectedStyles : keySelectBaseStyles}
      options={keySuggestions}
      value={selectedKeyOption}
      inputValue={keyInput}
      onInputChange={(val, meta) => {
        if (meta.action === 'input-change') {
          setKeyInput(sanitize(val));
        } else {
          setKeyInput(val);
        }
      }}
      onChange={(opt) => onKeyChange(opt?.value ?? null)}
      placeholder={keyPlaceholder}
      isDisabled={disabled}
      formatCreateLabel={(input) => `Use "${input}"`}
    />
  );

  return (
    <div className={classes.row}>
      {keyColumn}
      <CreatableSelect<Option, true>
        isMulti
        styles={valueSelectStyles}
        options={valueSuggestions}
        value={selectedValueOptions}
        inputValue={valueInput}
        onInputChange={(val, meta) => {
          if (meta.action === 'input-change') {
            setValueInput(sanitize(val));
          } else {
            setValueInput(val);
          }
        }}
        onChange={(opts) => onValuesChange(opts)}
        placeholder={valuePlaceholder}
        isDisabled={disabled}
        formatCreateLabel={(input) => `Add "${input}"`}
      />
      <button
        type="button"
        className={classes.removeButton}
        onClick={onRemove}
        disabled={disabled}
        aria-label="Remove row"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}

export function KeyValueInput({
  value,
  onChange,
  keySuggestions = [],
  valueSuggestions = {},
  disabled = false,
  disableSpaces = false,
  keyPlaceholder = 'Enter key...',
  valuePlaceholder = 'Type and press Enter...',
  keyVariant = 'select',
  reservedKeys,
  addLabel = '+ Add row',
  addOnDemand = false,
}: KeyValueInputProps) {
  const [rows, setRows] = useState<Row[]>(() => recordToRows(value, addOnDemand));

  // Sync internal rows from the external value (e.g. form reset / hydration on
  // edit). We compare against what the current rows *would emit* — that way
  // typing-in-progress (e.g. an empty-key row) doesn't get wiped each time
  // the form value reference churns, and a genuine external update always
  // re-syncs because rowsToRecord(rows) won't match the new value.
  useEffect(() => {
    setRows((prevRows) => {
      const prevRecord = rowsToRecord(prevRows);
      if (JSON.stringify(prevRecord) === JSON.stringify(value ?? {})) {
        return prevRows;
      }
      return recordToRows(value, addOnDemand);
    });
  }, [value, addOnDemand]);

  const usedKeys = useMemo(
    () => new Set(rows.map((r) => r.key).filter(Boolean)),
    [rows],
  );

  const normalizedReserved = useMemo(
    () => new Set((reservedKeys ?? []).map((k) => k.toLowerCase())),
    [reservedKeys],
  );

  // Per-row error message for the key input (only surfaced visually in
  // 'input' variant). Empty keys are never flagged.
  const keyErrors = useMemo(() => rows.map((row, index) => {
    const trimmed = row.key.trim();
    if (!trimmed) return undefined;
    const lowered = trimmed.toLowerCase();
    if (normalizedReserved.has(lowered)) return 'Already in use';
    const duplicate = rows.some(
      (other, i) => i !== index && other.key.trim().toLowerCase() === lowered,
    );
    if (duplicate) return 'Duplicate key';
    return undefined;
  }), [rows, normalizedReserved]);

  const updateRows = useCallback(
    (updatedRows: Row[]) => {
      setRows(updatedRows);
      onChange(rowsToRecord(updatedRows));
    },
    [onChange],
  );

  const handleKeyChange = useCallback(
    (index: number, newKey: string | null) => {
      const key = newKey ?? '';
      // Silent dedup only in 'select' variant — for the plain 'input' variant
      // we let duplicates through so the inline error has something to point
      // at (otherwise the user types a key and nothing visibly happens).
      if (
        keyVariant === 'select'
        && key
        && rows.some((row, i) => i !== index && row.key === key)
      ) {
        return;
      }
      const updated = rows.map((row, i) =>
        i === index ? { ...row, key } : row,
      );
      updateRows(updated);
    },
    [rows, updateRows, keyVariant],
  );

  const handleValuesChange = useCallback(
    (index: number, newValues: readonly Option[]) => {
      const updated = rows.map((row, i) =>
        i === index
          ? { ...row, values: newValues.map((v) => v.value) }
          : row,
      );
      updateRows(updated);
    },
    [rows, updateRows],
  );

  const handleRemoveRow = useCallback(
    (index: number) => {
      const updated = rows.filter((_, i) => i !== index);
      if (updated.length > 0 || addOnDemand) {
        updateRows(updated);
      } else {
        updateRows([{ id: nextRowId(), key: '', values: [] }]);
      }
    },
    [rows, updateRows, addOnDemand],
  );

  const handleAddRow = useCallback(() => {
    updateRows([...rows, { id: nextRowId(), key: '', values: [] }]);
  }, [rows, updateRows]);

  return (
    <div className={classes.container}>
      {rows.map((row, index) => {
        const availableKeySuggestions = keySuggestions.filter(
          (s) => s.value === row.key || !usedKeys.has(s.value),
        );
        const currentValueSuggestions = row.key
          ? (valueSuggestions[row.key] ?? [])
          : [];

        return (
          <KeyValueRow
            key={row.id}
            row={row}
            keyVariant={keyVariant}
            keySuggestions={availableKeySuggestions}
            valueSuggestions={currentValueSuggestions}
            keyError={keyVariant === 'input' ? keyErrors[index] : undefined}
            disabled={disabled}
            disableSpaces={disableSpaces}
            keyPlaceholder={keyPlaceholder}
            valuePlaceholder={valuePlaceholder}
            onKeyChange={(newKey) => handleKeyChange(index, newKey)}
            onValuesChange={(opts) => handleValuesChange(index, opts)}
            onRemove={() => handleRemoveRow(index)}
          />
        );
      })}
      <div className={classes.addButtonWrapper}>
        <button
          type="button"
          className={classes.addButton}
          onClick={handleAddRow}
          disabled={disabled}
        >
          {addLabel}
        </button>
      </div>
    </div>
  );
}
